import {useState, useContext, useRef, useEffect} from 'react';
import AuthContext from '../../context/AuthContext';
import {Box, Button, Divider, Modal, Typography} from '@mui/material';
import {useIdleTimer} from 'react-idle-timer';
import {useMediaQuery} from '@mui/material';
import {useTheme} from '@mui/material/styles';

/**
 * Component to handle auto-logout and session expiration warnings.
 *
 * @component
 * @returns {JSX.Element} The AutoLoggedOut component.
 */
const AutoLoggedOut = () => {
  const {userActive, logOutUser, pauseTimer} = useContext(AuthContext);
  const [isModelOpen, setIsModelOpen] = useState(false);
  const [countdown, setCountdown] = useState(10);
  const timer = useRef(null);
  const theme = useTheme();

  const isSmallScreen = useMediaQuery(theme => theme.breakpoints.down('sm'));
  const isGalaxyFold = useMediaQuery('(max-width:280px)');

  // Check if user is using org workflow
  const isOrgUser = userActive?.type === 'org';

  useEffect(() => {
    let countDownTimer;
    if (isModelOpen && countdown > 0) {
      countDownTimer = setTimeout(() => setCountdown(countdown - 1), 1000);
    } else if (isModelOpen && countdown === 0) {
      sessionLogOut();
    }

    return () => clearTimeout(countDownTimer);
  }, [isModelOpen, countdown]);

  // Handles session logout
  const sessionLogOut = () => {
    setIsModelOpen(false);
    clearTimeout(timer.current);
    timer.current = null;
    logOutUser();
  };

  //Handles action to keep session active
  const stayActive = () => {
    setCountdown(10);
    setIsModelOpen(false);
    clearTimeout(timer.current);
    timer.current = null;
  };

  //when the user is inactive
  const handleOnIdle = event => {
    // If no user is active, do nothing
    if (!userActive) return;

    // Handle org workflow
    if (isOrgUser) {
      setIsModelOpen(true);
      timer.current = setTimeout(sessionLogOut, 10000);
      return;
    }

    //keep session active if user is login and pauseTimer is true
    if (pauseTimer) {
      stayActive();
    } else {
      setIsModelOpen(true);
      timer.current = setTimeout(sessionLogOut, 10000);
    }
  };

  // Checks whether user has been inactive after 10 minutes period.
  const {getLastActiveTime} = useIdleTimer({
    timeout: 1000 * 60 * 10,
    onIdle: handleOnIdle,
    debounce: 500,
  });

  // Don't show modal if no user is active
  if (!userActive) return null;

  return (
    <Modal open={isModelOpen} aria-labelledby="modal-modal-title" aria-describedby="modal-modal-description">
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: isSmallScreen ? '80%' : 400,
          bgcolor: 'background.paper',
          outline: 'none',
          boxShadow: 24,
          borderRadius: '30px',
          p: isSmallScreen ? 2 : 4,
        }}
      >
        <Typography id="modal-modal-title" variant="h6" component="h6" sx={{fontSize: isGalaxyFold ? '14px' : '16px'}}>
          {isOrgUser ? 'Session Expiring' : 'Session Expire Warning'}
        </Typography>
        <Divider sx={{paddingY: 1}} />
        <Typography id="modal-modal-description" sx={{mt: 2, fontSize: isGalaxyFold ? '14px' : '16px'}}>
          Your session is about to expire. You will be {isOrgUser ? 'redirected' : 'logged out'} in {countdown} seconds.
        </Typography>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pt: 2,
          }}
        >
          <Button variant="text" onClick={() => sessionLogOut()}>
            {isOrgUser ? (isGalaxyFold ? 'Exit' : 'Exit now') : (isGalaxyFold ? 'Logout' : 'Log me out')}
          </Button>
          <Button variant="text" onClick={() => stayActive()}>
            {isGalaxyFold ? 'Stay' : 'Keep me signed in'}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default AutoLoggedOut;